import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Globe, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { API_URL } from '../lib/api';

const POLL_INTERVAL = 3000;

export default function WaitingRoom({ waitingId, meetingInfo, onAdmitted, onRejected }) {
  const [status, setStatus] = useState('waiting'); // waiting | admitted | rejected
  const intervalRef = useRef(null);

  // Persist waitingId for refresh recovery
  useEffect(() => {
    if (waitingId) {
      sessionStorage.setItem('pf_waitingId', waitingId);
    }
  }, [waitingId]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!waitingId) return;

    const poll = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/meetings/waiting/${waitingId}/status`);
        if (!resp.ok) return;
        const data = await resp.json();

        if (data.status === 'admitted') {
          stopPolling();
          setStatus('admitted');
          sessionStorage.removeItem('pf_waitingId');
          // Brief flash before transitioning
          setTimeout(() => {
            onAdmitted(data);
          }, 1200);
        } else if (data.status === 'rejected') {
          stopPolling();
          setStatus('rejected');
          sessionStorage.removeItem('pf_waitingId');
        }
      } catch (err) {
        // Silent — keep polling
      }
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL);
    return stopPolling;
  }, [waitingId, onAdmitted, stopPolling]);

  // Admitted flash
  if (status === 'admitted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="w-full max-w-md text-center">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Globe className="h-8 w-8 text-primary" />
            <span className="font-display font-bold text-xl">PocketFiler</span>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center py-12">
              <CheckCircle2 className="h-14 w-14 text-green-500 mb-4 animate-in zoom-in duration-300" />
              <p className="font-semibold text-lg text-green-700">You've been admitted!</p>
              <p className="text-sm text-muted-foreground mt-2">Connecting you to the meeting…</p>
              <Loader2 className="h-5 w-5 animate-spin text-primary mt-4" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Rejected
  if (status === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="w-full max-w-md text-center">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Globe className="h-8 w-8 text-primary" />
            <span className="font-display font-bold text-xl">PocketFiler</span>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center py-12">
              <XCircle className="h-14 w-14 text-destructive mb-4" />
              <p className="font-semibold text-lg">Unable to Join</p>
              <p className="text-sm text-muted-foreground mt-2 max-w-xs">
                The host has declined your request to join this meeting.
              </p>
              <Button
                variant="outline"
                className="mt-6"
                onClick={() => {
                  onRejected?.();
                  window.location.href = '/';
                }}
              >
                Go to PocketFiler
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Waiting state
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Globe className="h-8 w-8 text-primary" />
          <span className="font-display font-bold text-xl">PocketFiler</span>
        </div>

        <Card>
          <CardContent className="flex flex-col items-center py-12 px-6">
            {/* Animated spinner */}
            <div className="relative mb-6">
              <div className="h-16 w-16 rounded-full border-4 border-muted" />
              <div className="absolute inset-0 h-16 w-16 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            </div>

            <p className="font-semibold text-lg">
              {meetingInfo?.title || 'Meeting'}
            </p>
            {meetingInfo?.host_name && (
              <p className="text-sm text-muted-foreground mt-1">
                Hosted by {meetingInfo.host_name}
              </p>
            )}

            <p className="text-sm text-muted-foreground mt-6">
              Please wait for the host to let you in…
            </p>

            {/* Animated dots */}
            <div className="flex gap-1.5 mt-4">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-2 w-2 rounded-full bg-primary/60 animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground mt-4">
          You'll be connected automatically once admitted.
        </p>
      </div>
    </div>
  );
}
